import { useState } from 'react';
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import {
  Button,
  ListGroup,
  Table,
  Stack,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';

import { format } from 'date-fns';
import { formatTimeOrDateNow } from '@utils/DateUtils';
import DATE_FORMATS from '@constants/dateFormat';
import { ComHubHeadersEnum, DirectMessage } from '@company/types/comm-hub.type';
import { Link } from 'react-router-dom';
import { useDirectMessageContext } from '@company/state/directMessageContext';
import { useCompanyContext } from '@company/state/companyContext';
import {
  calculateReadUnreadDelta,
  createWorkerFromDirectMessage,
  getMessageIcon,
  handleMarkMessagesAsRead,
} from '@utils/ComHubUtils';
import { isAnnouncementChat } from '@constants/directChat';

import './styles.css';
import { Text } from '@mantine/core';

interface DirectMessagesTableProps {
  directMessages: DirectMessage[];
  title?: string;
  onShowWorkerProfile?: (workerId: number) => void;
  showHeader?: boolean;
  onCounterUpdate: (delta: number) => void;
  headerCount?: number;
}

function DirectMessagesTable({
  title,
  directMessages,
  onShowWorkerProfile,
  showHeader = true,
  onCounterUpdate,
  headerCount,
}: DirectMessagesTableProps) {
  const { openDirectMessageChat, markAsRead, markAsUnread } =
    useDirectMessageContext();
  const { indicators } = useCompanyContext();

  const handleOpenChat = (message: DirectMessage) => {
    const worker = createWorkerFromDirectMessage(message);
    openDirectMessageChat(worker, message.chatId, true, message, () => {
      // Only update counter on successful API call
      if (message.unreadMessages > 0) {
        onCounterUpdate(-1);
      }
    });
  };

  const headers = [
    ComHubHeadersEnum.CHAT_NAME,
    ComHubHeadersEnum.SUBJECT,
    ComHubHeadersEnum.ACTIONS_COLUMN,
  ];

  const shouldRenderHeader = showHeader && Boolean(title);

  const newDirectMessages = directMessages?.filter(
    (directMessage) => directMessage.unreadMessages > 0
  );

  const [isHovered, setIsHovered] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(null);

  const handleMarkAsRead = (messages: DirectMessage[]) => {
    const delta = calculateReadUnreadDelta(
      messages,
      (m) => m.unreadMessages > 0
    );

    handleMarkMessagesAsRead(
      messages,
      markAsRead,
      markAsUnread,
      (message) => message.chatId,
      (message) => message.unreadMessages > 0,
      () => onCounterUpdate(delta)
    );
  };

  return (
    <div className="th-table">
      {shouldRenderHeader && (
        <>
          <header
            className="d-flex align-items-center flex-wrap"
            style={{ minHeight: '38px' }}
          >
            <Link
              to="?tab=direct-messages"
              className="me-auto text-decoration-none align-self-end"
              style={{ color: 'inherit' }}
            >
              <h4 className="text-start">
                {title}{' '}
                {(headerCount !== undefined || indicators) && (
                  <b data-testid="new-counter">
                    ({headerCount ?? indicators?.unreadDirectChats ?? 0})
                  </b>
                )}
              </h4>
            </Link>
            {newDirectMessages.length ? (
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Mark new messages on page as read</Tooltip>}
              >
                <Button
                  type="button"
                  variant="light"
                  className="ms-auto fw-bold d-flex align-items-center"
                  style={{ fontSize: isMobile ? '12px' : '14px' }}
                  onClick={() => {
                    handleMarkAsRead(newDirectMessages);
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {newDirectMessages.length} new
                  {getMessageIcon(true, isHovered, isMobile)}
                </Button>
              </OverlayTrigger>
            ) : null}
          </header>
          <hr style={{ margin: '10px 0' }} />
        </>
      )}
      {directMessages?.length ? (
        <>
          <BrowserView>
            <Table>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* RENDER COLUMS BY ROW */}
                {directMessages.map((directMessage) => {
                  // const message = new DirectMessageModel(directMessage);
                  return (
                    <tr
                      key={directMessage.chatId}
                      className={`text-underline cursor-point ${
                        directMessage.unreadMessages ? 'unread-message' : ''
                      }`}
                      onMouseEnter={() =>
                        setHoveredMessageId(directMessage.chatId)
                      }
                      onMouseLeave={() => setHoveredMessageId(null)}
                      onClick={() => handleOpenChat(directMessage)}
                    >
                      <td style={{ width: '300px' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              !isAnnouncementChat(directMessage) &&
                              onShowWorkerProfile &&
                              directMessage.workerId
                            ) {
                              onShowWorkerProfile(directMessage.workerId);
                            }
                          }}
                          className={`text-capitalize ${
                            directMessage.unreadMessages ? 'unread-message' : ''
                          }`}
                        >
                          {directMessage.workerFullName}{' '}
                          {directMessage.messageCount > 1 &&
                            directMessage.messageCount}
                        </button>
                      </td>
                      <td>
                        <Text lineClamp={1} size="sm">
                          {directMessage.subject}
                        </Text>
                      </td>
                      {/* BUTTONS FOR ROW ACTIONS */}
                      <td className="actions text-end">
                        <div className="d-flex align-items-center justify-content-end h-100">
                          {hoveredMessageId === directMessage.chatId ? (
                            <>
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenChat(directMessage);
                                }}
                                className="action-btn me-2"
                              >
                                open
                              </Button>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>
                                    {`Mark as ${
                                      directMessage.unreadMessages
                                        ? 'read'
                                        : 'unread'
                                    }`}
                                  </Tooltip>
                                }
                              >
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead([directMessage]);
                                  }}
                                >
                                  {getMessageIcon(
                                    directMessage.unreadMessages > 0,
                                    hoveredMessageId === directMessage.chatId,
                                    isMobile
                                  )}
                                </button>
                              </OverlayTrigger>
                            </>
                          ) : (
                            <span className="date me-2">
                              {directMessage.lastMessageDateTime &&
                                formatTimeOrDateNow(
                                  directMessage.lastMessageDateTime
                                )}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </BrowserView>
          <MobileView>
            <ListGroup variant="flush">
              {directMessages.map((directMessage) => {
                return (
                  <ListGroup.Item
                    key={directMessage.chatId}
                    className={`ps-0 pe-0 border-0 d-flex gap-2 align-items-center ${
                      directMessage.unreadMessages ? 'unread-message' : ''
                    }`}
                    onClick={(e) => {
                      const dismissButton =
                        e.currentTarget.querySelector('.dismiss-button');
                      if (!dismissButton?.contains(e.target as Node)) {
                        handleOpenChat(directMessage);
                      }
                    }}
                  >
                    <Stack style={{ fontSize: '0.8rem' }}>
                      <span className="text-capitalize fw-bold fs-6">
                        {directMessage.workerFullName}{' '}
                        {directMessage.messageCount > 1 &&
                          directMessage.messageCount}
                      </span>
                      <span className="two-line-ellipsis">
                        {directMessage.subject}
                      </span>
                    </Stack>

                    <Stack className="ms-auto justify-content-around align-items-end">
                      <span className="text-nowrap">
                        {format(
                          new Date(directMessage.lastMessageDateTime),
                          DATE_FORMATS.SHORT_DATE_FORMAT
                        )}
                      </span>
                      <Button
                        variant="light"
                        size="sm"
                        className="p-1 dismiss-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead([directMessage]);
                        }}
                      >
                        {getMessageIcon(
                          directMessage.unreadMessages > 0,
                          false,
                          isMobile
                        )}
                      </Button>
                    </Stack>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </MobileView>
        </>
      ) : (
        <p className="mb-2 th-fs-14">No new notifications.</p>
      )}
    </div>
  );
}

export default DirectMessagesTable;
